import React, { useEffect, useState, useCallback, useRef } from 'react'
import { supabase } from '../lib/supabase/browser'
import { logger } from '../lib/logger'
import { useAuth } from './AuthContextCore'
import { NotificationsContext, type Notification } from './NotificationsContextCore'
import type { RealtimeChannel } from '@supabase/supabase-js'

export function NotificationsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const channelRef = useRef<RealtimeChannel | null>(null)

  const fetchNotifications = useCallback(async () => {
    if (!user?.id) {
      setNotifications([])
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      const { data, error: fetchError } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50)

      if (fetchError) throw fetchError

      setNotifications((data as Notification[]) || [])
    } catch (err) {
      logger.error('Error fetching notifications', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch notifications')
    } finally {
      setLoading(false)
    }
  }, [user?.id])

  const markAsRead = useCallback(async (notificationId: string) => {
    if (!user?.id) return

    try {
      // Optimistically update UI
      setNotifications(prev =>
        prev.map(n =>
          n.id === notificationId
            ? { ...n, read: true, read_at: new Date().toISOString() }
            : n
        )
      )

      const { error: updateError } = await supabase.rpc('mark_notification_as_read', {
        p_notification_id: notificationId
      })

      if (updateError) throw updateError
    } catch (err) {
      logger.error('Error marking notification as read', err)
      // Revert optimistic update on error
      fetchNotifications()
    }
  }, [user?.id, fetchNotifications])

  const markAllAsRead = useCallback(async () => {
    if (!user?.id) return

    try {
      // Optimistically update UI
      setNotifications(prev =>
        prev.map(n => ({
          ...n,
          read: true,
          read_at: new Date().toISOString()
        }))
      )

      const { error: updateError } = await supabase.rpc('mark_all_notifications_as_read', {
        p_user_id: user.id
      })

      if (updateError) throw updateError
    } catch (err) {
      logger.error('Error marking all notifications as read', err)
      // Revert optimistic update on error
      fetchNotifications()
    }
  }, [user?.id, fetchNotifications])

  // Set up real-time subscription for new notifications
  useEffect(() => {
    if (!user?.id) {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current)
        channelRef.current = null
      }
      return
    }

    // Create a subscription for new notifications
    const notificationChannel = supabase
      .channel(`notifications:${user.id}`, {
        config: {
          broadcast: { self: true }
        }
      })
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          logger.debug('New notification received', payload)
          
          // Add new notification to the list
          setNotifications(prev => [payload.new as Notification, ...prev])
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          logger.debug('Notification updated', payload)
          
          // Update notification in the list
          setNotifications(prev =>
            prev.map(n => (n.id === payload.new.id ? (payload.new as Notification) : n))
          )
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          logger.debug('Real-time subscription active')
        } else if (status === 'CHANNEL_ERROR') {
          logger.error('Real-time subscription error')
        }
      })

    channelRef.current = notificationChannel

    return () => {
      if (notificationChannel) {
        supabase.removeChannel(notificationChannel)
        if (channelRef.current === notificationChannel) {
          channelRef.current = null
        }
      }
    }
  }, [user?.id])

  // Fetch notifications on mount and when user changes
  useEffect(() => {
    fetchNotifications()
  }, [fetchNotifications])

  const unreadCount = notifications.filter(n => !n.read).length

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        error,
        markAsRead,
        markAllAsRead,
        refreshNotifications: fetchNotifications
      }}
    >
      {children}
    </NotificationsContext.Provider>
  )
}
